
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { Exhibit } from '@/data';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { MapPin } from 'lucide-react';

interface IndoorMapProps {
  mapImage: string;
  exhibits: Exhibit[];
}

const IndoorMap: React.FC<IndoorMapProps> = ({ mapImage, exhibits }) => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [activeExhibit, setActiveExhibit] = useState<Exhibit | null>(null);

  const handlePinClick = (exhibit: Exhibit) => {
    setActiveExhibit(exhibit);
  };

  const handleExhibitClick = (exhibitId: number) => {
    navigate(`/exhibit/${exhibitId}`);
  };

  return (
    <div className="w-full h-full flex items-center justify-center">
      <TransformWrapper
        initialScale={1}
        minScale={0.5}
        maxScale={4}
        limitToBounds={false}
        wheel={{ disabled: false }}
        pinch={{ disabled: false }}
        panning={{ disabled: true }}
        centerOnInit={true}
        doubleClick={{ disabled: true }}
      >
        {({ zoomIn, zoomOut }) => (
          <>
            <div className="absolute top-4 right-4 z-10 flex gap-2">
              <button
                onClick={() => zoomIn()}
                className="bg-white rounded-full p-2 shadow-md"
                aria-label="Zoom in"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
              </button>
              <button
                onClick={() => zoomOut()}
                className="bg-white rounded-full p-2 shadow-md"
                aria-label="Zoom out"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
              </button>
            </div>

            <TransformComponent 
              wrapperStyle={{ 
                width: "100%", 
                height: "100%", 
                display: "flex", 
                justifyContent: "center", 
                alignItems: "center" 
              }}
              contentStyle={{ 
                width: "100%", 
                height: "100%", 
                display: "flex", 
                justifyContent: "center", 
                alignItems: "center" 
              }}
            >
              <div className="relative">
                <img
                  src={mapImage}
                  alt={language === 'en' ? 'Tour Map' : 'מפת סיור'}
                  className="object-contain max-w-full max-h-full"
                />

                {exhibits.map((exhibit) => (
                  exhibit.location.x && exhibit.location.y ? (
                    <div
                      key={exhibit.id}
                      className="absolute cursor-pointer transform -translate-x-1/2 -translate-y-1/2"
                      style={{
                        left: `${exhibit.location.x}px`,
                        top: `${exhibit.location.y}px`,
                      }}
                      onClick={() => handlePinClick(exhibit)}
                    >
                      <MapPin
                        size={30}
                        className="text-museum fill-museum-shapes-red/50 stroke-museum"
                      />
                    </div>
                  ) : null
                ))}
              </div>
            </TransformComponent>
          </>
        )}
      </TransformWrapper>

      {/* Exhibit info popup */}
      {activeExhibit && (
        <div className="absolute bottom-4 left-4 right-4 bg-white rounded-lg shadow-lg p-4 z-20">
           <h3 className="font-bold text-lg whitespace-pre-line">
             {language === 'en' ? activeExhibit.titleEn : activeExhibit.titleHe}
           </h3>
          <p className="text-sm text-gray-600 mb-3">
            {language === 'en' ? 'Location: ' : 'מיקום: '}
            {activeExhibit.location.room}, {language === 'en' ? 'Floor' : 'קומה'} {activeExhibit.location.floor}
          </p>
          <button
            className="bg-museum text-white px-4 py-2 rounded-md w-full"
            onClick={() => handleExhibitClick(activeExhibit.id)}
          >
            {language === 'en' ? 'View Exhibit' : 'צפה במוצג'}
          </button>
        </div>
      )}
    </div>
  );
};

export default IndoorMap;
